import { TextAssist } from '../comments/TextAssist';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { V2Evidence } from '../../domain/evidence';
import { canCarryLimitedPortions, itemResultIncomplete, limitedPortionIncomplete, type ItemStatus, type LimitedPortion, type UnableReason, type V2Area, type V2ChecklistItem, type V2Inspection } from '../../domain/inspection';
import { limitationTemplateFor, renderLimitationTemplate, resolveLimitationTemplate } from '../../domain/authority/ny/limitationTemplates';
import { descriptorPromptsSafetyFinding, descriptorResolved, descriptorsForTemplate, itemDescriptorAnswer, type DescriptorDefinition, type HeatingFuel } from '../../domain/descriptors';
import { rowCanConfirmStructuralConcern } from '../../domain/inspectionRules';
import { useT } from '../../i18n/i18n';
import { aiConfidenceLabel, aiFailureMessage, aiVerdictNote, areaKindLabel, descriptorChoiceLabel, descriptorLabel, operationalTestLabel, resultLabel, severityLabel, unableReasonLabel, areaDisplayName, itemHintFor, itemTitleFor } from '../../i18n/labels';

// The three tiers in the result palette: red · amber · slate-blue (the report uses the fixed print colours).
const SEVERITY_TONES: Record<DefectSeverity, SegmentTone> = { safety: 'error', repair: 'defect', maintenance: 'primary' };
import { PortionRemovalConfirm, type PortionRemovalRequest } from '../areas/PortionRemovalConfirm';
import type { EvidenceTarget } from '../../state/captureFlow';
import { useStore } from '../../state/createStore';
import { evidenceForIds, evidenceStore } from '../../state/evidenceStore';
import { addLimitedPortionAction, changeItemResultAction, dismissEvidenceSuggestionAction, inspectionStore, removeLimitedPortionAction, replaceLimitedPortionAction, setItemDescriptorAction, setItemNoteAction, setItemOperationalTestAction, setItemReasonAction, setItemSeverityAction, setItemStructuralConcernAction, type ItemResultConfirmations } from '../../state/inspectionStore';
import { DEFECT_SEVERITY_ORDER, rowSeverity, type DefectSeverity } from '../../domain/severity';
import type { SegmentTone } from '../../ui/components/SegmentedControl';
import { aiStore, observationsShownForItem } from '../../state/aiStore';
import { Button, Chip, ConfirmSheet, Field, InlineMessage, SectionEyebrow, SegmentedControl, SheetShell, TextArea, TextInput, ToggleChip } from '../../ui/components';
import { formatClock, useAutosaveText } from '../../ui/hooks';
import { AdvancedReviewBlock } from '../ai/AdvancedReviewBlock';
import { ReportWordingBlock, useWordingWriteAvailability } from './ReportWordingBlock';
import { templateRowScope } from '../../domain/checklistTemplates';
import { checkTypeOfTemplateRow, checkTypeRecordsTest } from '../../report/rowWording';
import { RESULT_OPTIONS } from './resultOptions';
import { UnableReasonSheet } from '../areas/UnableReasonSheet';
import type { LightboxContext } from '../evidence/Lightbox';
import { PhotoStrip, Thumbnail } from '../evidence/PhotoStrip';

// THE CHECKLIST ITEM — six blocks, fixed order, nothing else (Final Owner Decision 2026-09-14, CA-6;
// order changed 2026-10-27 after the device walkthrough — the result sat 3,000 pt down, behind 20
// photo buttons — Owner: 「检查项单子把结果提到最上面」):
//   ① 检查结果          无异常 · 缺陷 · 无法检查 · 不适用 — the inspector's decision. Since 2026-10-27 a row the
//                       AI read as a defect comes prefilled as 缺陷 (prepareAiDefectDefaults); changing it to
//                       无异常 asks once (the AI's reading is overruled knowingly, CA-11 stands)
//   ② AI报告语          the report sentence (the App Language first, editable · the other language ·
//                       professional · AI write / AI regenerate from the inspector's own words)
//   ③ 关联照片          the ONE evidence entry of the row (AI-placed and hand-linked alike; a removal is
//                       an override nothing automatic undoes — CA-7). AI-placed photos are linked, marked
//                       「AI 放的」, and undone with 「不是这项」
//   ④ AI观察与可能的问题  the row's AI observations, the inspector's note (the ONE note field), the
//                       advanced review entry / result / conversation
//   ⑤ 未观察部位        the frozen New York limitation rules (Unable reason · partial portions)
//   ⑥ 检查要点          the row's frozen guidance
// No finding editor, no accept gate, no second note, no second photo picker, no seventh block.
// Owner device test 2026-09-21 (「AI观察语是对的。应该做一个根据观察语生成报告语的选项」): ④ carries ONE
// button that asks ② to write the sentence from the row's AI observations. It sets no result, creates
// no finding and accepts nothing — the inspector's result still rules the sentence.
export function ItemSheet(props: {
  item: V2ChecklistItem;
  area: V2Area;
  inspection: V2Inspection;
  inspectionId: string;
  itemIndex: number;
  itemCount: number;
  locked?: boolean;
  onClose: () => void;
  onOpenLightbox: (evidence: V2Evidence[], index: number, context: LightboxContext) => void;
  onNextItem: () => void;
  hasNextItem: boolean;
  // Opened from the report's 「去记录」 for one descriptor: bring that descriptor on screen (issue 12).
  focusDescriptor?: string | null;
}) {
  const { t, tp, locale } = useT();
  const item = props.item;
  const aiState = useStore(aiStore, (s) => s);
  // 2026-10-28 (device walkthrough A6): with a defect reading on the row, its "no visible defect" lines are not listed.
  const observations = useMemo(() => observationsShownForItem(aiState, item.id), [aiState, item.id]);
  const locked = props.locked === true;
  const hint = itemHintFor(locale, item);
  const rowScope = templateRowScope(item.templateId);
  // TW-3, on every check whose report sentence depends on it since 2026-09-29: the inspector records
  // whether the test / operation was actually done (alarm test button, GFCI, windows …).
  const checkType = checkTypeOfTemplateRow(item.templateId);
  const testFact = checkType !== null && checkTypeRecordsTest(checkType) ? operationalTestLabel(t, checkType) : null;
  const structuralRow = rowCanConfirmStructuralConcern(item.templateId);
  const evidenceState = useStore(evidenceStore, (s) => s);
  // 2026-10-27: the photos the AI PLACED on this row (「AI 放的」) are linked evidence like the rest; they are
  // shown apart so the inspector sees what the AI did and can undo a wrong one with 「不是这项」.
  const suggested = useMemo(() => evidenceForIds(evidenceState, item.evidenceSuggestions), [evidenceState, item.evidenceSuggestions]);
  const linked = useMemo(() => evidenceForIds(evidenceState, item.evidenceIds.filter((id) => !item.evidenceSuggestions.includes(id))), [evidenceState, item.evidenceIds, item.evidenceSuggestions]);
  const [statusSavedAt, setStatusSavedAt] = useState<number | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  // 2026-10-27: 无异常 chosen on a row the AI read as a defect — asked once before it is written.
  const [clearDefect, setClearDefect] = useState<string | null>(null);
  // NY F1 R3 narrow repair (F-R3-04): the confirmation for a result that drops the recorded parts not observed.
  const [portionRemoval, setPortionRemoval] = useState<PortionRemovalRequest | null>(null);
  const [unableOpen, setUnableOpen] = useState(false);
  // NY F1 Round 3 (OD-5 = A): the quick reason sheet opened in "part of it" mode — a new portion
  // (index null) or one being edited.
  const [portionEditor, setPortionEditor] = useState<{ index: number | null; portion: LimitedPortion | null } | null>(null);
  const photosRef = useRef<HTMLElement | null>(null);
  const wordingRef = useRef<HTMLElement | null>(null);
  // The button under ③: a counter ⑥ listens to (the write, its confirmation and its errors live there).
  const [observationWriteSignal, setObservationWriteSignal] = useState(0);
  const wordingWrite = useWordingWriteAvailability(item);
  // The mandatory descriptors this row carries (none for most rows); the property's heating fuel
  // suggests the water heater's energy source (#9 → #6), one tap to confirm.
  const descriptorDefs = descriptorsForTemplate(item.templateId);
  const heatingFuel = useStore(inspectionStore, (s) => s.inspections.find((i) => i.id === props.inspectionId)?.property.heatingFuel ?? null);
  // Android acceptance 2026-10-21 (issue 12): the report's 「去记录」 used to open the row at the top, and on
  // a row with many suggested photos the descriptor was far below. Open at the descriptor it named.
  const focusDescriptor = props.focusDescriptor ?? null;
  useEffect(() => {
    if (!focusDescriptor) return;
    const timer = window.setTimeout(() => {
      const target = document.querySelector<HTMLElement>(`[data-testid="descriptor-${focusDescriptor}"]`) ?? document.querySelector<HTMLElement>('[data-testid="item-descriptors"]') ?? document.querySelector<HTMLElement>('[data-testid="item-block"][data-block="1"]');
      // jsdom has no scrollIntoView; a WebView always does.
      if (target && typeof target.scrollIntoView === 'function') target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [focusDescriptor]);

  const saveNote = useCallback((value: string) => setItemNoteAction(item.id, value), [item.id]);
  const note = useAutosaveText(item.inspectorNote, saveNote);
  const saveReason = useCallback((value: string) => setItemReasonAction(item.id, value), [item.id]);
  const reason = useAutosaveText(item.statusReason, saveReason);

  // Every transition runs through changeItemResultAction; a result that cannot carry the recorded
  // parts not observed asks first (R3 narrow repair, F-R3-04). The answer is passed back so the gate
  // never asks twice. The inspector is the only caller — no AI path reaches this (CA-11).
  async function applyResult(next: ItemStatus, unable?: { reasonCode: UnableReason; note: string }, confirmed: ItemResultConfirmations = {}) {
    const outcome = await changeItemResultAction(item.id, next, { ...confirmed, unable });
    if (!outcome.ok) {
      setPortionRemoval({ status: next, unable, portions: outcome.portions, confirmed });
      return;
    }
    setStatusSavedAt(Date.now());
  }

  async function chooseResult(next: ItemStatus) {
    if (locked) return;
    if (next === 'unableToInspect') {
      setUnableOpen(true);
      return;
    }
    if (next === item.status) return;
    // 2026-10-27: the AI read a defect here — 无异常 overrules it, and is asked for once.
    if (next === 'checkedOk') {
      const defect = observations.find((o) => o.verdict === 'defect');
      if (defect) {
        setClearDefect(defect.headline.trim() || defect.observation);
        return;
      }
    }
    await applyResult(next);
  }

  // The quick reason sheet states in its own notice that the whole-row Unable replaces the recorded
  // parts not observed — that confirmation travels with the transition (F-R3-04).
  async function confirmUnable(code: UnableReason, unableNote: string) {
    setUnableOpen(false);
    await applyResult('unableToInspect', { reasonCode: code, note: unableNote }, { confirmPortionRemoval: true });
  }

  async function clearResult() {
    setConfirmClear(false);
    await applyResult('notChecked');
  }

  function scrollToPhotos() {
    // jsdom has no scrollIntoView; a WebView always does.
    if (typeof photosRef.current?.scrollIntoView === 'function') photosRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  function writeFromObservations() {
    setObservationWriteSignal((n) => n + 1);
    // The sentence lands in ⑥: bring it into view so the inspector reads what was written.
    if (typeof wordingRef.current?.scrollIntoView === 'function') wordingRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  // 2026-10-27: an AI-placed photo is evidence already; the inspector's one tap undoes a wrong one —
  // the removal override, so no later run places it here again.
  async function dismissSuggestion(evidenceId: string) {
    if (locked) return;
    await dismissEvidenceSuggestionAction(item.id, evidenceId);
  }

  const target: EvidenceTarget = { inspectionId: props.inspectionId, areaId: props.area.id, itemId: item.id, findingId: null };
  const lastSaved = Math.max(statusSavedAt ?? 0, note.savedAt ?? 0, reason.savedAt ?? 0);
  const unableIncomplete = itemResultIncomplete(item);
  // OD-7 = C: with no note typed, the frozen NY wording is what the report states for `obstructed`.
  const wholeTemplate = item.status === 'unableToInspect' && item.reasonCode && item.statusReason.trim().length === 0 ? limitationTemplateFor(item.reasonCode) : null;
  const wholeTemplateText = wholeTemplate ? renderLimitationTemplate(wholeTemplate, { locale, component: itemTitleFor(locale, item), extent: '', scope: 'whole', method: null }) : null;

  const footer = (
    <Button variant={props.hasNextItem ? 'primary' : 'secondary'} trailingIcon="arrow_forward" onClick={props.onNextItem} disabled={!props.hasNextItem} testId="next-item">
      {/* Android acceptance 2026-10-21 (issue 8): 「没有未检查的项了」 while THIS item still had no result. */}
      {props.hasNextItem ? t('item.nextItem') : item.status === 'notChecked' ? t('item.lastUnchecked') : t('item.noNext')}
    </Button>
  );

  return (
    <SheetShell full title={itemTitleFor(locale, item)} subtitle={t('item.metaOf', { index: props.itemIndex + 1, total: props.itemCount, area: areaDisplayName(locale, props.area) })} onClose={props.onClose} testId="item-sheet" footer={footer}>
      {/* ① 检查结果 */}
      <section className="workspace-card item-block" data-testid="item-block" data-block="1">
        <SectionEyebrow tone="primary">{t('item.blockResult')}</SectionEyebrow>
        <div className="item-state-grid">
          <SegmentedControl
            variant="tiles"
            ariaLabel={t('item.status')}
            value={item.status === 'notChecked' ? null : item.status}
            onChange={(next) => void chooseResult(next)}
            options={RESULT_OPTIONS.map((option) => ({ key: option.status, label: resultLabel(t, option.status), tone: option.tone, icon: option.icon, disabled: locked, testId: `status-${option.status}` }))}
            testId="status-grid"
          />
        </div>
        {/* 2026-10-19 (Owner decision list item 16): every defect carries a tier. The inspector chooses it;
            until then the row shows the suggested tier, marked as such. */}
        {item.status === 'issueFound' ? (
          <div className="reason-block" data-testid="item-severity" data-severity={rowSeverity(item)} data-chosen={item.severity !== null ? '1' : '0'}>
            <SectionEyebrow>{t('severity.title')}</SectionEyebrow>
            <SegmentedControl
              variant="compact"
              block
              ariaLabel={t('severity.title')}
              value={rowSeverity(item)}
              onChange={(next) => {
                if (!locked) void setItemSeverityAction(item.id, next);
              }}
              options={DEFECT_SEVERITY_ORDER.map((key) => ({ key, label: severityLabel(t, key), tone: SEVERITY_TONES[key], disabled: locked, testId: `severity-${key}` }))}
              testId="severity-control"
            />
            <p className="card-note">{item.severity === null ? t('severity.suggestedHint') : t('severity.chosenHint')}</p>
          </div>
        ) : null}
        {testFact && (item.status === 'checkedOk' || item.status === 'issueFound') ? (
          <div className="reason-block" data-testid="item-test-block" data-check-type={checkType ?? undefined} data-on={item.operationalTestPerformed ? '1' : '0'}>
            <ToggleChip
              label={testFact.label}
              on={item.operationalTestPerformed}
              onToggle={(on) => {
                if (!locked) void setItemOperationalTestAction(item.id, on);
              }}
              testId="item-test-toggle"
            />
            <p className="card-note">{testFact.hint}</p>
          </div>
        ) : null}
        {/* Ratification 2026-09-13 §4: the inspector-confirmed structural fact — on the row, small. */}
        {structuralRow && item.status === 'issueFound' ? (
          <div className="reason-block" data-testid="item-structural" data-on={item.structuralConcern ? '1' : '0'}>
            <ToggleChip
              label={t('finding.structuralConcern')}
              on={item.structuralConcern}
              onToggle={(on) => {
                if (!locked) void setItemStructuralConcernAction(item.id, on);
              }}
              testId="item-structural-toggle"
            />
            <p className="card-note">{t('finding.structuralConcernHint')}</p>
          </div>
        ) : null}
        {descriptorDefs.length > 0 && (item.status === 'checkedOk' || item.status === 'issueFound') ? (
          <div className="reason-block" data-testid="item-descriptors">
            <SectionEyebrow>{t('descriptor.sectionTitle')}</SectionEyebrow>
            <p className="card-note">{t('descriptor.sectionHint')}</p>
            {descriptorDefs.map((definition) => (
              <DescriptorControl key={definition.key} definition={definition} item={item} heatingFuel={heatingFuel} locked={locked} onRecordDefect={() => void chooseResult('issueFound')} />
            ))}
          </div>
        ) : null}
        {item.status === 'notApplicable' ? (
          <div className="reason-block" data-testid="na-block">
            <Field label={t('item.optionalNote')} htmlFor="item-reason">
              <TextArea id="item-reason" value={reason.value} onChange={reason.setValue} placeholder={t('item.naNotePlaceholder')} rows={2} disabled={locked} testId="item-reason" />
            </Field>
          </div>
        ) : null}
        {item.status !== 'notChecked' && !locked ? (
          <div className="status-tools">
            <Button variant="text" size="small" block={false} onClick={() => setConfirmClear(true)} testId="clear-result">
              {t('area.clearResult')}
            </Button>
          </div>
        ) : null}
      </section>

      {/* ② AI报告语 */}
      <section ref={wordingRef} className="workspace-card item-block" data-testid="item-block" data-block="2">
        <SectionEyebrow tone="primary">{t('item.blockWording')}</SectionEyebrow>
        <ReportWordingBlock item={item} area={props.area} observationWriteSignal={observationWriteSignal} />
      </section>

      {/* ③ 关联照片 */}
      <section ref={photosRef} className="workspace-card item-block" data-testid="item-block" data-block="3">
        <SectionEyebrow tone="primary">{`${t('item.blockPhotos')} (${item.evidenceIds.length})`}</SectionEyebrow>
        {suggested.length > 0 ? (
          <div className="evidence-suggestions" data-testid="item-evidence-suggestions" data-count={suggested.length}>
            <SectionEyebrow>{`${t('ai.suggestedEvidenceTitle')} (${suggested.length})`}</SectionEyebrow>
            <p className="card-note">{t('ai.suggestedEvidenceHint')}</p>
            <div className="photo-row evidence-suggestion-grid">
              {suggested.map((ev, index) => (
                <div key={ev.id} className="evidence-suggestion" data-testid="item-evidence-suggestion" data-evidence-id={ev.id}>
                  <Thumbnail evidence={ev} onClick={() => props.onOpenLightbox(suggested, index, { itemId: item.id, findingId: null })} />
                  {!locked ? (
                    <div className="evidence-suggestion-actions">
                      <Button variant="text" size="small" onClick={() => void dismissSuggestion(ev.id)} testId="item-evidence-dismiss">
                        {t('ai.dismissEvidence')}
                      </Button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <PhotoStrip
          evidence={linked}
          target={target}
          layout="grid"
          actions="pair"
          pool={{ itemId: item.id, findingId: null, linkedIds: item.evidenceIds }}
          disabled={locked}
          onOpen={(index) => props.onOpenLightbox(linked, index, { itemId: item.id, findingId: null })}
          emptyText={suggested.length > 0 ? '' : t('item.evidenceEmpty')}
          testId="item-photos"
        />
        <p className="card-note">{tp('common.photosCount', item.evidenceIds.length)}</p>
      </section>

      {/* ④ AI观察与可能的问题 */}
      <section className="workspace-card item-block ai-item-block" data-testid="item-block" data-block="4" data-observations={observations.length}>
        <SectionEyebrow tone="primary">{t('item.blockAi')}</SectionEyebrow>
        {observations.length === 0 ? (
          <p className="card-note" data-testid="item-ai-none">
            {t('ai.noneForRow')}
          </p>
        ) : (
          observations.map((o) => {
            const body = (o.observationUi ?? o.observation).trim();
            const headline = o.headline.trim();
            const verdictNote = aiVerdictNote(t, o.verdict);
            // 2026-10-19 (Owner: 「什么问题。哪里有问题。不要长篇大论」): the headline — the problem and the place — is
            // the first line; the one-sentence observation is the second; a clarification leads with its question.
            return (
              <div key={o.id} className="ai-observation" data-testid="item-ai-observation" data-observation-id={o.id} data-verdict={o.verdict ?? ''}>
                {o.type === 'needsClarification' ? (
                  <>
                    <p className="card-body ai-observation-headline" data-testid="item-ai-headline">
                      <strong>{t('ai.toVerify')}</strong>：{o.question ?? headline}
                    </p>
                    {body.length > 0 && body !== (o.question ?? headline) ? <p className="card-note">{body}</p> : null}
                  </>
                ) : (
                  <>
                    <p className="card-body ai-observation-headline" data-testid="item-ai-headline">
                      <strong>{headline.length > 0 ? headline : t('ai.observation')}</strong>
                    </p>
                    {body.length > 0 && body !== headline ? <p className="card-note">{body}</p> : null}
                  </>
                )}
                {o.concern.trim().length > 0 ? (
                  <p className="card-note">
                    <strong>{t('ai.concern')}</strong>：{o.concern}
                  </p>
                ) : null}
                {/* 2026-10-18: the model's confidence was in the protocol since Round 2 and never on the screen. */}
                <div className="ai-observation-meta">
                  <span className="ai-confidence" data-testid="item-ai-confidence" data-confidence={o.confidence}>
                    {aiConfidenceLabel(t, o.confidence)}
                  </span>
                </div>
                {verdictNote ? (
                  <p className="card-note ai-verdict-note" data-testid="item-ai-verdict-note" data-verdict={o.verdict ?? ''}>
                    {verdictNote}
                  </p>
                ) : null}
              </div>
            );
          })
        )}
        <p className="card-note">{t('ai.evidenceNote')}</p>
        {observations.length > 0 && !locked ? (
          <div className="ai-observation-write">
            <Button variant="tonal" icon="psychology" small onClick={writeFromObservations} disabled={!wordingWrite.available} testId="item-ai-write-wording">
              {wordingWrite.busy ? t('wording.writing') : t('wording.fromObservation')}
            </Button>
            <p className="card-note" data-testid="item-ai-write-wording-note">
              {!wordingWrite.recorded
                ? t('wording.fromObservationNeedsResult')
                : !wordingWrite.online
                  ? t('ai.needsOnline')
                  : wordingWrite.policyBlock !== null
                    ? aiFailureMessage(t, wordingWrite.policyBlock)
                    : t('wording.fromObservationHint')}
            </p>
          </div>
        ) : null}
        <Field label={t('item.inspectorNote')} htmlFor="item-note" hint={t('item.inspectorNoteHint')}>
          <TextArea id="item-note" value={note.value} onChange={note.setValue} placeholder={t('item.notePlaceholder')} rows={3} disabled={locked} testId="item-note" />
          <TextAssist value={note.value} onChange={note.setValue} fieldId="item-note" lang={locale} disabled={locked} testId="item-note-assist" />
        </Field>
        <AdvancedReviewBlock inspection={props.inspection} area={props.area} item={item} locked={locked} onAdjustPhotos={scrollToPhotos} />
      </section>

      {/* ⑤ 未观察部位 — the frozen New York rules, unchanged (CA-12) */}
      <section className="workspace-card item-block" data-testid="item-block" data-block="5">
        <SectionEyebrow tone="primary">{t('item.blockPortions')}</SectionEyebrow>
        {item.status === 'unableToInspect' ? (
          <div className="reason-block" data-testid="reason-block">
            <SectionEyebrow>{t('item.reason')}</SectionEyebrow>
            <div className="reason-summary">
              <span className="badge badge-muted" data-testid="reason-current">
                {item.reasonCode ? unableReasonLabel(t, item.reasonCode) : t('unable.noReason')}
              </span>
              {item.statusReason.length > 0 ? <span className="card-note">{item.statusReason}</span> : null}
              <Button variant="text" size="small" block={false} onClick={() => setUnableOpen(true)} disabled={locked} testId="unable-change">
                {item.reasonCode ? t('unable.change') : t('unable.choose')}
              </Button>
            </div>
            {wholeTemplateText ? (
              <p className="unable-report-reason" data-testid="unable-template-preview">
                <strong>{t('unable.reportReason')}</strong>
                {wholeTemplateText}
              </p>
            ) : null}
            {unableIncomplete ? <InlineMessage kind="err" text={item.reasonCode === null ? t('item.unableIncomplete') : t('item.unableNeedsNote')} /> : null}
          </div>
        ) : null}
        {canCarryLimitedPortions(item.status) ? (
          <div className="reason-block" data-testid="limited-portions" data-count={item.limitedPortions.length}>
            {item.limitedPortions.length === 0 ? <p className="card-note">{t('item.limitedPortionsHint')}</p> : null}
            <div className="portion-list">
              {item.limitedPortions.map((portion, index) => {
                const typed = portion.note.trim().length > 0;
                const template = typed ? null : resolveLimitationTemplate(portion.reasonCode, portion.templateId);
                const text = typed ? portion.note : template ? renderLimitationTemplate(template, { locale, component: itemTitleFor(locale, item), extent: portion.extent, scope: 'partial', method: null }) : '';
                return (
                  <div key={`${index}-${portion.reasonCode}`} className="portion-row" data-testid="limited-portion" data-reason={portion.reasonCode}>
                    <span className="badge badge-muted">{unableReasonLabel(t, portion.reasonCode)}</span>
                    <span className="portion-extent">{portion.extent.length > 0 ? portion.extent : t('item.portionUnnamed')}</span>
                    {!locked ? (
                      <span className="portion-actions">
                        <Button variant="text" size="small" block={false} onClick={() => setPortionEditor({ index, portion })} testId="portion-edit">
                          {t('item.portionEdit')}
                        </Button>
                        <Button variant="text" size="small" block={false} onClick={() => void removeLimitedPortionAction(item.id, index)} testId="portion-remove">
                          {t('item.portionRemove')}
                        </Button>
                      </span>
                    ) : null}
                    {text.length > 0 ? (
                      <span className="card-note portion-text" data-testid="portion-text">
                        {text}
                      </span>
                    ) : null}
                    {limitedPortionIncomplete(portion) ? <InlineMessage kind="err" text={t('item.portionNeedsNote')} /> : null}
                  </div>
                );
              })}
            </div>
            {!locked ? (
              <Button variant="secondary" size="small" block={false} icon="add" onClick={() => setPortionEditor({ index: null, portion: null })} testId="add-limited-portion">
                {t('item.addLimitedPortion')}
              </Button>
            ) : null}
          </div>
        ) : null}
        {item.status === 'notApplicable' ? <p className="card-note">{t('item.reportWordingNone')}</p> : null}
      </section>

      {/* ⑥ 检查要点 */}
      <section className="workspace-card item-block" data-testid="item-block" data-block="6">
        <SectionEyebrow tone="primary">{t('item.blockGuidance')}</SectionEyebrow>
        <p className="eyebrow">{areaKindLabel(t, props.area.kind)}</p>
        {hint.length > 0 ? (
          <p className="card-body" data-testid="item-hint">
            {hint}
          </p>
        ) : (
          <p className="card-note" data-testid="item-hint">
            {t('item.guidanceNone')}
          </p>
        )}
        {rowScope === 'supplemental' || rowScope === 'methodLimitation' ? (
          <p className="card-note item-scope" data-testid="item-scope" data-scope={rowScope}>
            {t(rowScope === 'supplemental' ? 'item.scopeSupplemental' : 'item.scopeMethod')}
          </p>
        ) : null}
      </section>

      <p className="saved-note" data-testid="item-saved-note">
        {t('item.saved')}
        {lastSaved > 0 ? ` · ${t('common.autosaved', { time: formatClock(lastSaved) })}` : ''}
      </p>

      {unableOpen ? (
        <UnableReasonSheet
          itemTitle={itemTitleFor(locale, item)}
          rowTemplateId={item.templateId}
          itemStatus={item.status}
          existingPortions={item.limitedPortions.length}
          initialReason={item.reasonCode}
          initialNote={item.statusReason}
          onConfirm={(code, unableNote) => void confirmUnable(code, unableNote)}
          onConfirmPortion={(portion) => {
            setUnableOpen(false);
            void addLimitedPortionAction(item.id, portion);
          }}
          onClose={() => setUnableOpen(false)}
        />
      ) : null}
      {portionEditor ? (
        <UnableReasonSheet
          itemTitle={itemTitleFor(locale, item)}
          rowTemplateId={item.templateId}
          itemStatus={item.status}
          existingPortions={item.limitedPortions.length}
          initialScope="partial"
          initialReason={null}
          initialNote=""
          initialPortion={portionEditor.portion}
          onConfirm={(code, unableNote) => {
            setPortionEditor(null);
            void confirmUnable(code, unableNote);
          }}
          onConfirmPortion={(portion) => {
            const index = portionEditor.index;
            setPortionEditor(null);
            void (index === null ? addLimitedPortionAction(item.id, portion) : replaceLimitedPortionAction(item.id, index, portion));
          }}
          onClose={() => setPortionEditor(null)}
        />
      ) : null}
      {confirmClear ? (
        <ConfirmSheet title={t('area.clearResultTitle')} body={t('area.clearResultBody')} confirmLabel={t('area.clearResult')} onConfirm={() => void clearResult()} onCancel={() => setConfirmClear(false)} />
      ) : null}
      {clearDefect !== null ? (
        <ConfirmSheet
          title={t('ai.clearDefectTitle')}
          body={t('ai.clearDefectBody', { headline: clearDefect })}
          confirmLabel={t('ai.clearDefectConfirm')}
          onConfirm={() => {
            setClearDefect(null);
            void applyResult('checkedOk');
          }}
          onCancel={() => setClearDefect(null)}
        />
      ) : null}
      {portionRemoval ? (
        <PortionRemovalConfirm
          request={portionRemoval}
          onConfirm={() => {
            const req = portionRemoval;
            setPortionRemoval(null);
            void applyResult(req.status, req.unable, { ...req.confirmed, confirmPortionRemoval: true });
          }}
          onCancel={() => setPortionRemoval(null)}
        />
      ) : null}
    </SheetShell>
  );
}

// NY F1 Round 2 (OD-1 = A1, Owner constraints): ONE descriptor of the current row — chips for a
// choice, one short line for text, an optional detail line after a choice. Everything autosaves like
// the rest of the item; an unresolved mandatory descriptor shows a plain "needed for the report" line
// and NEVER blocks the row, the area or the next item (OC-2: the field pass is not interrupted; the
// gap is closed at closeout). Tapping the chosen chip again clears the answer — never to Unknown.
// An absent smoke / CO detector prompts the inspector to mark the row a Defect (the row IS the finding).
function DescriptorControl(props: { definition: DescriptorDefinition; item: V2ChecklistItem; heatingFuel: HeatingFuel | null; locked: boolean; onRecordDefect: () => void }) {
  const { t } = useT();
  const { definition, item } = props;
  const answer = itemDescriptorAnswer(item, definition.key);
  const resolved = descriptorResolved(definition, answer);
  const currentValue = answer?.value ?? '';
  const currentDetail = answer?.detail ?? '';
  const saveText = useCallback((value: string) => setItemDescriptorAction(item.id, definition.key, value, currentDetail), [item.id, definition.key, currentDetail]);
  const text = useAutosaveText(definition.kind === 'text' ? currentValue : '', saveText);
  const saveDetail = useCallback((value: string) => setItemDescriptorAction(item.id, definition.key, currentValue, value), [item.id, definition.key, currentValue]);
  const detail = useAutosaveText(currentDetail, saveDetail);
  const suggested = definition.prefillFromHeatingFuel && !answer && props.heatingFuel !== null && definition.choices.includes(props.heatingFuel) ? props.heatingFuel : null;

  function choose(choice: string) {
    if (props.locked) return;
    void setItemDescriptorAction(item.id, definition.key, currentValue === choice ? '' : choice, currentDetail);
  }

  return (
    <div className="reason-block" data-testid={`descriptor-${definition.key}`} data-resolved={resolved ? 'true' : 'false'}>
      <p className="field-label">{descriptorLabel(t, definition.key)}</p>
      {definition.kind === 'choice' ? (
        <div className="chips chips-reason">
          {definition.choices.map((choice) => (
            <Chip key={choice} label={descriptorChoiceLabel(t, definition.key, choice)} selected={currentValue === choice} onClick={() => choose(choice)} testId={`descriptor-${definition.key}-${choice}`} />
          ))}
        </div>
      ) : (
        <TextInput value={text.value} onChange={text.setValue} placeholder={t('descriptor.textPlaceholder')} disabled={props.locked} testId={`descriptor-${definition.key}-text`} />
      )}
      {suggested ? <p className="card-note">{t('descriptor.prefilled')}</p> : null}
      {definition.detail && answer ? <TextInput value={detail.value} onChange={detail.setValue} placeholder={t('descriptor.detailPlaceholder')} disabled={props.locked} testId={`descriptor-${definition.key}-detail`} /> : null}
      {!resolved ? (
        <p className="blockers-note" data-testid={`descriptor-${definition.key}-needed`}>
          {t('descriptor.needed')}
        </p>
      ) : null}
      {descriptorPromptsSafetyFinding(definition, answer) ? (
        <>
          <InlineMessage kind="err" text={t('descriptor.safetyPrompt')} />
          {!props.locked && item.status !== 'issueFound' ? (
            <Button variant="secondary" size="small" block={false} icon="warning" onClick={props.onRecordDefect} testId={`descriptor-${definition.key}-safety`}>
              {t('descriptor.recordSafetyFinding')}
            </Button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
